<!--
	BootstrapVue Compatibility: BFormCheckbox
	
	CREATED: Dec 2024 - Vue 3 Migration
	WHO: GitHub Copilot (Claude Sonnet 4.5)
	WHY: BootstrapVue b-form-checkbox is not compatible with Vue 3
	
	PURPOSE:
	- Provides a Vue 3 compatible drop-in replacement for b-form-checkbox
	- Mimics BootstrapVue's API for seamless migration
	- Supports value/unchecked-value pattern used throughout the application
	- Uses native HTML checkbox with Bootstrap 5 classes for styling
	
	PROPS MAPPING (BootstrapVue → This Component):
	- v-model → v-model (works with modelValue)
	- value → value (the value when checked)
	- unchecked-value → uncheckedValue (the value when unchecked)
	- name → name
	- id → id
	- disabled → disabled
	- required → required
	- state → state (validation state: true/false/null)
	- size → size (sm/lg)
	- inline → inline (for inline layout)
	- switch → switch (renders as switch instead of checkbox)
	
	USAGE EXAMPLES:
	Basic checkbox:
	<b-form-checkbox v-model="isActive">Active</b-form-checkbox>
	
	With value/unchecked-value (common pattern in this app):
	<b-form-checkbox v-model="folder.active" value="Y" unchecked-value="N" />
	
	Explicit binding (for reactive array items in v-for loops):
	<b-form-checkbox 
		:model-value="folder.active" 
		@update:modelValue="folder.active = $event"
		value="Y" 
		unchecked-value="N" 
	/>
	
	With validation state:
	<b-form-checkbox v-model="agreed" :state="agreedValid" required>
		I agree to terms
	</b-form-checkbox>
	
	Switch style:
	<b-form-checkbox v-model="enabled" switch>Enable feature</b-form-checkbox>
	
	KNOWN ISSUES:
	- In some cases with reactive proxy objects, v-model may not bind correctly.
	  Use explicit :model-value and @update:modelValue binding as shown above.
	- String comparison is case-insensitive ("Y"="y", "N"="n")
-->

<template>
	<div class="form-check" :class="checkboxClasses">
		<input
			:id="computedId"
			type="checkbox"
			class="form-check-input"
			:class="inputClasses"
			:name="name"
			:checked="isChecked"
			:disabled="disabled"
			:required="required"
			:aria-describedby="ariaDescribedby"
			:aria-invalid="state === false ? 'true' : null"
			@change="handleChange"
		/>
		<label v-if="hasLabel" :for="computedId" class="form-check-label">
			<slot />
		</label>
	</div>
</template>

<script>
/**
 * BFormCheckbox - Vue 3 Compatible BootstrapVue Replacement
 * 
 * IMPLEMENTATION NOTES:
 * - Uses native <input type="checkbox"> with Bootstrap 5 form-check classes
 * - Supports BootstrapVue's value/unchecked-value pattern for non-boolean checkboxes
 * - Case-insensitive string comparison for "Y"/"N", "yes"/"no" values
 * - Emits: update:modelValue, change, input (for maximum compatibility)
 * 
 * VALUE HANDLING:
 * - When checked: emits the `value` prop (default: true)
 * - When unchecked: emits the `uncheckedValue` prop (default: false)
 * - String values are compared case-insensitively ("Y" === "y")
 * 
 * REACTIVITY:
 * - isChecked computed property re-evaluates when modelValue or value changes
 * - Compatible with Vue 3 Proxy objects and reactive arrays
 * 
 * EXAMPLE:
 * <b-form-checkbox v-model="folder.active" value="Y" unchecked-value="N" />
 * - If folder.active === "Y" → checkbox is checked
 * - If folder.active === "N" → checkbox is unchecked
 * - Clicking updates folder.active to "Y" or "N" accordingly
 */

export default {
	name: 'BFormCheckbox',
	props: {
		modelValue: {
			type: [String, Number, Boolean, Array],
			default: null
		},
		value: {
			type: [String, Number, Boolean],
			default: true
		},
		uncheckedValue: {
			type: [String, Number, Boolean],
			default: false
		},
		name: {
			type: String,
			default: null
		},
		id: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		state: {
			type: Boolean,
			default: null
		},
		size: {
			type: String,
			default: null,
			validator: value => ['sm', 'lg', null].includes(value)
		},
		inline: {
			type: Boolean,
			default: false
		},
		switch: {
			type: Boolean,
			default: false
		},
		ariaDescribedby: {
			type: String,
			default: null
		}
	},
	emits: ['update:modelValue', 'change', 'input'],
	computed: {
		computedId() {
			return this.id || `checkbox-${Math.random().toString(36).substr(2, 9)}`
		},
		isChecked() {
			// Simple case-insensitive comparison
			// Convert to strings and compare (handles "Y"/"N", "y"/"n", etc.)
			const modelStr = String(this.modelValue || '').toUpperCase()
			const valueStr = String(this.value || '').toUpperCase()
			return modelStr === valueStr
		},
		hasLabel() {
			return !!this.$slots.default
		},
		checkboxClasses() {
			return {
				'form-check-inline': this.inline,
				'form-switch': this.switch,
				[`form-control-${this.size}`]: this.size
			}
		},
		inputClasses() {
			return {
				'is-valid': this.state === true,
				'is-invalid': this.state === false
			}
		}
	},
	methods: {
		handleChange(event) {
			const isChecked = event.target.checked
			// Simple: emit value when checked, uncheckedValue when unchecked
			const newValue = isChecked ? this.value : this.uncheckedValue

			this.$emit('update:modelValue', newValue)
			this.$emit('change', newValue)
			this.$emit('input', newValue)
		}
	}
}
</script>

<style scoped>
/* Bootstrap 5 form-check styles are applied via classes */
</style>
